// ============================================================
// HOUSE OF CARDS — TomeAvatar.js — THE MAN IN THE HOUSE
// ------------------------------------------------------------
// Tome, standing in his own house, carrying his own lamp.
//
// Zero assets. Zero .glb. Zero Blender. He is eleven primitives
// and a walk cycle, and he is a WOODCUT, not a model: near-black
// coat, a bone ink-line around his silhouette, and the only thing
// that saves him from being a hole in the frame is the lantern in
// his fist under-lighting him from below.
//
// He is PURELY PRESENTATIONAL. He owns no state. The rig in
// SceneEngine3D mutates three refs every frame and Tome reads them:
//   posRef     THREE.Vector3   where he is standing (y = floor)
//   facingRef  { current:num } radians. 0 = looking at the back wall.
//   speedRef   { current:num } 0 = still, 1 = walking, ~1.7 = running
// Refs, not props, ON PURPOSE — he animates at 60fps and React must
// never re-render for it.
//
// THE LANTERN IS THE LIGHTING FIX. The house is dark because the
// house is DARK — that's the game. But the light now walks with you,
// so wherever Tome is, you can see, and everywhere he isn't stays
// black. Diegetic. Free dread. It also gives every object in the
// room a moving key light, which is what makes 3D read as 3D.
//
// SCALE: TOME_H = 2.9 world units. The rooms are 5.2–7.4 high, so
// that puts him at a bit under half the ceiling — a grand Victorian
// manor with 4m ceilings. Everything in the house is measured
// against him now; he IS the scale reference the rooms never had.
// ============================================================
import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { RM } from "./theme";

// ── HIS DIMENSIONS — imported by the rig. Never hardcode these. ──
export const TOME_H = 2.9;    // floor to crown
export const TOME_EYE = 2.55; // eye height (first-person, later)
export const TOME_R = 0.55;   // his footprint. This is what the walls clamp against.

const REDUCED =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ── THE INK PALETTE ─────────────────────────────────────────────
const COAT = "#15100c";     // the coat is almost the dark itself
const COAT_L = "#241a13";   // shoulders, sleeves — one step up
const BOOT = "#0b0806";
const SKIN = "#7a6046";     // dim. His face is not the point. The shape is.
const HAIR = "#120c08";
const BRASS = "#8a6a2e";
const GLASS = "#f0a04b";
const FLAME = "#ffd9a0";
const LINE = RM.boneFaint;  // the woodcut contour

// ── LANTERN — the one light that moves ──────────────────────────
const LANTERN_I = 7.4;      // intensity at gain 1
const LANTERN_D = 11.0;     // how far down the hall it throws
const LANTERN_DECAY = 1.35; // < 2 = it carries. This is a lamp, not a candle.

export default function TomeAvatar({
  posRef,
  facingRef,
  speedRef,
  psychosis = 0,
  eyesActive = false,
  gain = 1,
}) {
  const root = useRef();   // position only — never rotates
  const yaw = useRef();    // rotation.y = facing
  const body = useRef();   // bob / lean / sway
  const legL = useRef();
  const legR = useRef();
  const armL = useRef();
  const armR = useRef();   // the lantern arm
  const lamp = useRef();   // lantern group (swings)
  const light = useRef();  // the pointLight itself
  const flame = useRef();
  const shade = useRef();  // his contact shadow — which does NOT always obey him

  const phase = useRef(0);
  const swing = useRef(0);

  // Under the eyes the lamp goes bruise-violet and stops being a comfort.
  const lampColor = useMemo(
    () => new THREE.Color(eyesActive ? "#8b2f7a" : RM.emberBright),
    [eyesActive]
  );

  useFrame((st, delta) => {
    const dt = Math.min(0.05, delta);
    const t = st.clock.elapsedTime;

    const g = root.current;
    if (!g) return;

    // ── WHERE HE IS ────────────────────────────────────────────
    const p = posRef.current;
    g.position.set(p.x, 0, p.z);
    if (yaw.current) yaw.current.rotation.y = facingRef.current;

    const sp = Math.min(1.6, speedRef.current || 0);

    // ── THE WALK ───────────────────────────────────────────────
    // Phase advances with SPEED, not with time. Stop moving and the
    // legs settle instead of moonwalking on the spot.
    if (!REDUCED) phase.current += dt * sp * 9.0;
    const ph = phase.current;

    const set = (ref, target, k = 0.3) => {
      if (ref.current) ref.current.rotation.x += (target - ref.current.rotation.x) * k;
    };
    set(legL, Math.sin(ph) * 0.55 * sp);
    set(legR, -Math.sin(ph) * 0.55 * sp);
    set(armL, -Math.sin(ph) * 0.44 * sp);
    // the lantern arm barely swings. He is being careful with it.
    set(armR, 0.16 + Math.sin(ph) * 0.09 * sp);

    if (body.current) {
      const b = body.current;
      b.position.y = Math.abs(Math.sin(ph)) * 0.06 * sp;      // bob
      b.rotation.z = Math.sin(ph) * 0.035 * sp;               // sway
      b.rotation.x = -0.05 * sp;                              // lean into the walk
      // standing still, he breathes. Nothing static ever looks alive.
      const breath = REDUCED ? 0 : Math.sin(t * 1.1) * 0.010 * (1 - Math.min(1, sp));
      b.scale.set(1, 1 + breath, 1);
    }

    // ── THE LANTERN ────────────────────────────────────────────
    // It lags behind him. It swings when he stops. It is the only
    // honest thing in the house.
    const want = Math.sin(ph * 0.5) * 0.26 * sp;
    swing.current += (want - swing.current) * 0.07;
    if (lamp.current) lamp.current.rotation.z = swing.current;

    // it GUTTERS — harder the further Tome's mind goes
    const gutter =
      0.88 +
      Math.sin(t * 11.3) * (0.05 + psychosis * 0.10) +
      Math.sin(t * 29.1) * (0.035 + psychosis * 0.09);
    const dying = 1 - psychosis * 0.34;   // his light fails as he does
    if (light.current) {
      light.current.intensity = LANTERN_I * gain * gutter * dying * (eyesActive ? 0.62 : 1);
      light.current.color.copy(lampColor);
    }
    if (flame.current) {
      flame.current.scale.setScalar(0.9 + gutter * 0.18);
    }

    // ── HIS SHADOW ─────────────────────────────────────────────
    // At LUCID it sits under his boots where a shadow belongs.
    // Past SPLITTING it starts arriving a half-second late, and from
    // the wrong direction, and it is bigger than he is. Nobody will
    // tell you this is happening. You will just start to notice.
    if (shade.current) {
      const drift = Math.max(0, psychosis - 0.45) / 0.55;
      shade.current.position.x = Math.sin(t * 0.37) * 0.62 * drift;
      shade.current.position.z = Math.cos(t * 0.29) * 0.62 * drift;
      shade.current.scale.setScalar(1 + drift * 0.4);
      shade.current.material.opacity = 0.48 + drift * 0.12;
    }
  });

  return (
    <group ref={root}>
      {/* CONTACT SHADOW — y 0.038. Outside the yaw group ON PURPOSE:
          it must be free to stop following him. */}
      <mesh ref={shade} position={[0, 0.038, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.58, 22]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.48} depthWrite={false} />
      </mesh>

      <group ref={yaw}>
        <group ref={body}>
          {/* ── LEGS — pivot at the hip (y 1.15) ── */}
          <group ref={legL} position={[-0.145, 1.15, 0]}>
            <mesh position={[0, -0.53, 0]} castShadow>
              <cylinderGeometry args={[0.105, 0.085, 1.05, 8]} />
              <meshStandardMaterial color={COAT} roughness={0.98} />
            </mesh>
            <mesh position={[0, -1.10, 0.06]} castShadow>
              <boxGeometry args={[0.19, 0.11, 0.32]} />
              <meshStandardMaterial color={BOOT} roughness={1} />
            </mesh>
          </group>
          <group ref={legR} position={[0.145, 1.15, 0]}>
            <mesh position={[0, -0.53, 0]} castShadow>
              <cylinderGeometry args={[0.105, 0.085, 1.05, 8]} />
              <meshStandardMaterial color={COAT} roughness={0.98} />
            </mesh>
            <mesh position={[0, -1.10, 0.06]} castShadow>
              <boxGeometry args={[0.19, 0.11, 0.32]} />
              <meshStandardMaterial color={BOOT} roughness={1} />
            </mesh>
          </group>

          {/* ── THE COAT — one open cylinder, flared at the hem.
              Torso and coat in a single shape, because a silhouette
              is a silhouette. Hem 1.01, collar 2.43. ── */}
          <mesh position={[0, 1.72, 0]} castShadow>
            <cylinderGeometry args={[0.34, 0.60, 1.42, 14, 1, true]} />
            <meshStandardMaterial
              color={COAT}
              roughness={0.97}
              side={THREE.DoubleSide}
              emissive={RM.emberDim}
              emissiveIntensity={0.10}
            />
          </mesh>
          {/* the ink line — a backface shell, 4% bigger. This is the
              whole reason he looks HAND-CUT and not like a mannequin
              somebody imported. Costs one draw call. */}
          <mesh position={[0, 1.72, 0]} scale={[1.045, 1.02, 1.045]}>
            <cylinderGeometry args={[0.34, 0.60, 1.42, 14, 1, true]} />
            <meshBasicMaterial color={LINE} side={THREE.BackSide} toneMapped={false} />
          </mesh>

          {/* ── SHOULDERS / COLLAR ── */}
          <mesh position={[0, 2.34, 0]} scale={[1, 0.5, 0.72]} castShadow>
            <sphereGeometry args={[0.42, 14, 10]} />
            <meshStandardMaterial color={COAT_L} roughness={0.96} />
          </mesh>
          <mesh position={[0, 2.34, 0]} scale={[1.05, 0.53, 0.76]}>
            <sphereGeometry args={[0.42, 14, 10]} />
            <meshBasicMaterial color={LINE} side={THREE.BackSide} toneMapped={false} />
          </mesh>

          {/* ── NECK / HEAD / HAIR ── */}
          <mesh position={[0, 2.46, 0]}>
            <cylinderGeometry args={[0.075, 0.09, 0.17, 8]} />
            <meshStandardMaterial color={SKIN} roughness={1} />
          </mesh>
          <mesh position={[0, 2.68, 0.01]} scale={[1, 1.14, 0.98]} castShadow>
            <sphereGeometry args={[0.185, 14, 12]} />
            <meshStandardMaterial
              color={SKIN}
              roughness={1}
              emissive="#2a1a10"
              emissiveIntensity={0.25}
            />
          </mesh>
          <mesh position={[0, 2.68, 0.01]} scale={[1.06, 1.2, 1.04]}>
            <sphereGeometry args={[0.185, 14, 12]} />
            <meshBasicMaterial color={LINE} side={THREE.BackSide} toneMapped={false} />
          </mesh>
          {/* cropped hair. He's a builder, not a dandy. */}
          <mesh position={[0, 2.76, -0.025]} scale={[1.06, 0.8, 1.06]}>
            <sphereGeometry args={[0.19, 12, 10]} />
            <meshStandardMaterial color={HAIR} roughness={1} />
          </mesh>

          {/* ── LEFT ARM — swings ── */}
          <group ref={armL} position={[-0.40, 2.32, 0]}>
            <mesh position={[0, -0.46, 0]} castShadow>
              <cylinderGeometry args={[0.09, 0.075, 0.92, 8]} />
              <meshStandardMaterial color={COAT_L} roughness={0.95} />
            </mesh>
            <mesh position={[0, -0.96, 0.02]}>
              <sphereGeometry args={[0.075, 8, 6]} />
              <meshStandardMaterial color={SKIN} roughness={1} />
            </mesh>
          </group>

          {/* ── RIGHT ARM — carries the light ── */}
          <group ref={armR} position={[0.40, 2.32, 0]}>
            <mesh position={[0, -0.46, 0]} castShadow>
              <cylinderGeometry args={[0.09, 0.075, 0.92, 8]} />
              <meshStandardMaterial color={COAT_L} roughness={0.95} />
            </mesh>
            <mesh position={[0, -0.96, 0.02]}>
              <sphereGeometry args={[0.075, 8, 6]} />
              <meshStandardMaterial color={SKIN} roughness={1} />
            </mesh>

            {/* ── THE LANTERN ─────────────────────────────────── */}
            <group ref={lamp} position={[0, -1.06, 0.05]}>
              {/* handle */}
              <mesh position={[0, 0.15, 0]} rotation={[Math.PI / 2, 0, 0]}>
                <torusGeometry args={[0.05, 0.012, 6, 12]} />
                <meshStandardMaterial color={BRASS} metalness={0.6} roughness={0.5} />
              </mesh>
              {/* cap */}
              <mesh position={[0, 0.075, 0]}>
                <coneGeometry args={[0.11, 0.09, 8]} />
                <meshStandardMaterial color={BRASS} metalness={0.5} roughness={0.6} />
              </mesh>
              {/* glass */}
              <mesh>
                <cylinderGeometry args={[0.085, 0.095, 0.17, 8, 1, true]} />
                <meshStandardMaterial
                  color={GLASS}
                  emissive={GLASS}
                  emissiveIntensity={2.0}
                  transparent
                  opacity={0.48}
                  side={THREE.DoubleSide}
                  toneMapped={false}
                />
              </mesh>
              {/* the flame — blows past white, because it is FIRE.
                  This is the only toneMapped={false} that earns it. */}
              <mesh ref={flame}>
                <sphereGeometry args={[0.05, 8, 6]} />
                <meshBasicMaterial color={FLAME} toneMapped={false} fog={false} />
              </mesh>
              {/* base */}
              <mesh position={[0, -0.105, 0]}>
                <cylinderGeometry args={[0.1, 0.08, 0.045, 8]} />
                <meshStandardMaterial color={BRASS} metalness={0.5} roughness={0.6} />
              </mesh>

              {/* THE LIGHT. castShadow is deliberately OFF — a
                  shadow-casting point light is six shadow renders a
                  frame, and the ceiling lamp already pays that bill.
                  This one is here to LIGHT, and it does. */}
              <pointLight
                ref={light}
                color={RM.emberBright}
                intensity={LANTERN_I * gain}
                distance={LANTERN_D}
                decay={LANTERN_DECAY}
              />
            </group>
          </group>
        </group>
      </group>
    </group>
  );
}
